import { Form } from 'antd';
import dayjs from 'dayjs';
import React, { FC, memo, useEffect } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { Button } from 'shared/ui/Button';
import { DatePicker } from 'shared/ui/DatePicker';
import { Input } from 'shared/ui/Input';
import { CLIENT_DATE_FORMAT, languageToLabelMap } from 'shared/utils/constants';
import { AppLanguage } from 'app/config/i18Config/types';
import { Select } from 'shared/ui/Select';
import { datePickerValueUnder18, disableFutureDatesUnder18 } from 'shared/utils/helpers/disable18YearsDatePicker';
import { ChangeUserDetails } from 'features/ChangeUserDetails';
import { TextArea } from 'shared/ui/TextArea';
import { useRole } from 'entities/User';
import { getGlobalSettings } from 'app/appState/model/selectors/getGlobalSettings';
import { PhoneInput } from 'shared/ui/PhoneInput';
import { PHONE_NUMBER_REGEX } from 'shared/utils/regex';

interface CustomerDetailsFormProps {
  userId: string;
  language: string;
  email: string;
  dateOfBirth?: Nullable<string>;
  additionalEmail?: Nullable<string>;
  additionalPhone?: Nullable<string>;
  phone?: Nullable<string>;
  bankingRequisites?: Nullable<string>;
}

export const CustomerDetailsForm: FC<CustomerDetailsFormProps> = memo((props) => {
  const { userId, dateOfBirth, email, additionalEmail, bankingRequisites, language, phone, additionalPhone } = props;

  const { t } = useAppTranslation(['profile', 'common']);
  const globalSettings = useAppSelector(getGlobalSettings);

  const { isPartner } = useRole();

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      language,
      additionalEmail,
      additionalPhone,
      bankingRequisites,
      dateOfBirth: dateOfBirth ? dayjs(dateOfBirth) : undefined,
    });
  }, [additionalEmail, dateOfBirth, form, language, bankingRequisites, additionalPhone]);

  const languages = globalSettings?.availableLanguages
    .filter((lang: string): lang is AppLanguage => Object.values(AppLanguage).includes(lang as AppLanguage))
    .map((language: AppLanguage) => ({
      label: languageToLabelMap[language],
      value: language,
    }));

  return (
    <div className="mt-10">
      <div className="mb-7 font-semibold text-2xl">{t('Customer details')}</div>
      <Form className="space-y-2" name="customerDetailsForm" form={form} layout="vertical" disabled={isPartner}>
        <div className="flex items-center space-x-7">
          <Form.Item
            className="w-full"
            name="additionalEmail"
            rules={[
              { type: 'email', message: t('Please enter a correct additional email!') },
              {
                validator: async (_, value) => {
                  value && value === email
                    ? await Promise.reject(new Error(t('Additional email must be different from the main email!')))
                    : await Promise.resolve();
                },
              },
            ]}
          >
            <Input
              bordered
              placeholder={t('Additional email')}
              tooltip={t('Please enter an additional email address to receive copies of bills')}
              label={t('Additional Email')}
            />
          </Form.Item>
          <Form.Item
            className="w-full"
            name="additionalPhone"
            label={t('Additional phone')}
            rules={[
              {
                pattern: PHONE_NUMBER_REGEX,
                message: t('Please provide a valid phone number. Example: +123456789012345'),
              },
              {
                validator: async (_, value) => {
                  value && value === phone
                    ? await Promise.reject(new Error(t('Additional phone must be different from the main phone!')))
                    : await Promise.resolve();
                },
              },
            ]}
          >
            <PhoneInput placeholder={t('Enter phone')} bordered initialValue={additionalPhone} />
          </Form.Item>
        </div>
        <div className="flex items-center space-x-7">
          <Form.Item className="w-full" name="dateOfBirth" label={t('Date of Birth')}>
            <DatePicker
              bordered
              className="h-[52px] w-full"
              format={CLIENT_DATE_FORMAT}
              disabledDate={disableFutureDatesUnder18}
              defaultPickerValue={datePickerValueUnder18}
            />
          </Form.Item>
          <Form.Item
            className="w-full"
            name="language"
            label={t('Language')}
            rules={[{ required: true, message: t('Please select language!') }]}
          >
            <Select placeholder={t('Select language')} options={languages || []} bordered />
          </Form.Item>
        </div>

        <div className="flex items-center space-x-7">
          <Form.Item className="w-1/2" name="bankingRequisites" label={t('Banking requisites')}>
            <TextArea bordered placeholder={t('Banking requisites')} />
          </Form.Item>
          <ChangeUserDetails
            userId={userId}
            formInstance={form}
            action={
              <Button containerClassName="basis-1/2" size="large" isDisabled={isPartner}>
                {t('Change customer details')}
              </Button>
            }
          />
        </div>
      </Form>
    </div>
  );
});
